import type { EjsSystem } from "./ejs";

// The whole opening sequence as numbered steps: CloudBoy's own (the emulator
// page loading, the game being read from the browser) then, for EmulatorJS
// games, the stages its progress line goes through.

export interface OpeningStep {
  number: number;
  total: number;
  label: string;
  /** EmulatorJS's progress for this step ("45%", "12.3MB"), if it gave one. */
  detail: string | null;
}

export function stepLabels(system: EjsSystem | null): string[] {
  const own = ["Loading the emulator", "Reading the game"];
  if (!system) return own;
  return [
    ...own,
    "Downloading the emulator core",
    "Unpacking the emulator core",
    "Loading the game",
    "Unpacking the game",
    "Starting the game",
  ];
}

export interface OpeningState {
  system: EjsSystem | null;
  /** The emulator page itself is still loading. */
  booting: boolean;
  /** EmulatorJS has been handed the game. */
  ejsStarted: boolean;
  /** EmulatorJS's progress line, if it has one yet. */
  ejsText: string | null;
  /** EmulatorJS has shown "Download Game Data" at some point. */
  sawGameDownload: boolean;
}

const MESSAGES: [string, string][] = [
  ["Download Game Core", "Downloading the emulator core"],
  ["Decompress Game Core", "Unpacking the emulator core"],
  ["Download Game Data", "Loading the game"],
];

export function openingStep(state: OpeningState): OpeningStep {
  const labels = stepLabels(state.system);
  const at = (label: string, detail: string | null = null): OpeningStep => ({
    number: labels.indexOf(label) + 1,
    total: labels.length,
    label,
    detail,
  });
  if (state.booting) return at("Loading the emulator");
  if (!state.system || !state.ejsStarted) return at("Reading the game");
  const text = state.ejsText ?? "";
  for (const [message, label] of MESSAGES) {
    if (text.startsWith(message)) return at(label, text.slice(message.length).trim() || null);
  }
  if (text.startsWith("Decompress Game Data")) {
    const detail = text.slice("Decompress Game Data".length).trim() || null;
    return at("Unpacking the game", detail);
  }
  // No line yet means the core is being fetched; anything after the game's
  // own steps is the core booting it.
  return at(state.sawGameDownload ? "Starting the game" : "Downloading the emulator core");
}
